import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { eq, or } from 'tio-ui/utilities';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import { PhCaretDown, PhCaretUp, PhWarningCircle } from 'ember-phosphor-icons';
import IssueNotificationCard from 'tio-employee/components/reimagined/issue-notification-card';
import AddressSelectionCard from 'tio-employee/components/reimagined/address-selection-card';
import type {
  ContributionActionItem,
  ContributionActionTask,
} from 'tio-employee/services/reimagined-data';
import type PersonModel from 'tio-data/models/person';

interface GroupedIssueNotificationCardSignature {
  Args: {
    item: ContributionActionItem;
    person: PersonModel;
    onAddressSubmit: (item: ContributionActionItem, addressId: string) => Promise<void>;
  };
  Element: HTMLDivElement;
}

export default class GroupedIssueNotificationCard extends Component<GroupedIssueNotificationCardSignature> {
  @tracked isExpanded = false;

  get tasks() {
    return this.args.item.tasks ?? [];
  }

  get issueLabels() {
    return [...new Set(this.tasks.flatMap((task) => task.issueLabels))];
  }

  getAddressOptions = (task: ContributionActionTask) => {
    const addresses = (task.metadata?.available_addresses ?? []) as Array<{
      id: string;
      label: string;
    }>;
    return addresses;
  };

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  handleAddressSubmit = (addressId: string) => {
    return this.args.onAddressSubmit(this.args.item, addressId);
  };

  <template>
    <div
      class="bg-white border border-gray-200 flex flex-col gap-5 items-start px-4 py-6 rounded-2xl"
      data-test-grouped-issue-notification-card
      ...attributes
    >
      <button
        type="button"
        class="flex items-center justify-between w-full cursor-pointer"
        data-test-grouped-issue-toggle
        aria-expanded={{this.isExpanded}}
        aria-label={{if
          this.isExpanded
          (t "slr.reimagined.collapse_action_item")
          (t "slr.reimagined.expand_action_item")
        }}
        {{on "click" this.toggleExpanded}}
      >
        <div class="flex gap-3 items-center">
          <div class="flex items-center justify-center rounded-lg shrink-0 size-12 bg-amber-100">
            <PhWarningCircle @size={{32}} class="text-amber-600" />
          </div>
          <div class="flex flex-col gap-2 items-start text-left">
            <div class="flex items-center gap-2">
              <Text class="font-semibold text-base text-gray-700 tracking-tight leading-snug">
                {{@item.title}}
              </Text>
              {{#if @item.badge}}
                <Chip @intent={{@item.badgeIntent}} @size="sm">
                  {{@item.badge}}
                </Chip>
              {{/if}}
            </div>
            <Text class="font-medium text-base text-gray-500 leading-snug">
              {{@item.description}}
            </Text>
            {{#if this.issueLabels.length}}
              <div class="flex flex-wrap gap-1.5">
                {{#each this.issueLabels as |label|}}
                  <Chip @intent="warning" @size="sm" data-test-grouped-issue-label>
                    {{label}}
                  </Chip>
                {{/each}}
              </div>
            {{/if}}
          </div>
        </div>
        <div class="flex items-center justify-center shrink-0 ml-4">
          {{#if this.isExpanded}}
            <PhCaretUp @size={{24}} class="text-gray-700" />
          {{else}}
            <PhCaretDown @size={{24}} class="text-gray-700" />
          {{/if}}
        </div>
      </button>

      {{#if this.isExpanded}}
        <div
          class="bg-sky-50 flex flex-col items-start p-4 rounded-2xl w-full gap-4"
          data-test-grouped-issue-expanded
        >
          <Text class="text-base text-gray-700 leading-relaxed">
            {{t "slr.reimagined.grouped_tasks_heading"}}
          </Text>

          <div class="flex flex-col gap-4 w-full">
            {{#each this.tasks as |task|}}
              <div
                class="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col gap-3"
                data-test-grouped-issue-task={{task.id}}
              >
                <div class="flex gap-3 items-start">
                  <div
                    class="mt-0.5 size-5 rounded border-2 border-amber-500 bg-amber-50 shrink-0"
                    aria-hidden="true"
                  ></div>
                  <div class="flex flex-col gap-2 min-w-0">
                    <Text class="font-semibold text-base text-gray-800 leading-snug">
                      {{task.title}}
                    </Text>
                    <Text class="text-sm font-medium text-gray-500 leading-relaxed">
                      {{task.description}}
                    </Text>
                    {{#if task.issueLabels.length}}
                      <div class="flex flex-wrap gap-1.5">
                        {{#each task.issueLabels as |label|}}
                          <Chip @intent="warning" @size="sm">
                            {{label}}
                          </Chip>
                        {{/each}}
                      </div>
                    {{/if}}
                  </div>
                </div>

                {{#if (eq task.resolutionType "upload")}}
                  <IssueNotificationCard
                    @title={{task.title}}
                    @description={{task.description}}
                    @person={{@person}}
                    @successMessage={{or task.successMessage @item.successMessage}}
                    data-test-grouped-issue-upload-card
                  />
                {{else if (eq task.resolutionType "dropdown")}}
                  <AddressSelectionCard
                    @title={{task.title}}
                    @description={{task.description}}
                    @addresses={{this.getAddressOptions task}}
                    @onSubmit={{this.handleAddressSubmit}}
                    data-test-grouped-issue-address-card
                  />
                {{/if}}
              </div>
            {{/each}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
